import React, { useEffect, useState } from 'react';
import { useFriendStore } from '../../store/friendStore';
import { useMessageStore } from '../../store/messageStore';
import { useServerStore } from '../../store/serverStore';
import { UserPlus, UserMinus, MessageSquare, Check, X, Users, AlertCircle } from 'lucide-react';
import { Skeleton } from '../ui/Skeleton';

const FriendsList: React.FC = () => {
  const { friends, pendingRequests, fetchFriends, fetchPendingRequests, sendFriendRequest, acceptFriendRequest, rejectFriendRequest, removeFriend, subscribeToFriendRequests, error, isLoading } = useFriendStore();
  const { setCurrentDmUser } = useMessageStore();
  const { setCurrentChannel } = useServerStore();
  const [username, setUsername] = useState('');
  const [requestError, setRequestError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'pending'>('all');
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  useEffect(() => {
    fetchFriends();
    fetchPendingRequests();
    
    // Subscribe to friend requests
    const unsubscribe = subscribeToFriendRequests();
    
    return () => {
      unsubscribe();
    };
  }, [fetchFriends, fetchPendingRequests, subscribeToFriendRequests]);

  const handleSendRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsLoadingRequest(true);
      setRequestError(null);
      await sendFriendRequest(username);
      setUsername('');
    } catch (err: any) {
      setRequestError(err.message);
    } finally {
      setIsLoadingRequest(false);
    }
  };

  const handleMessageFriend = (friendId: string) => {
    setCurrentChannel(null);
    setCurrentDmUser(friendId);
  };

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="p-4 border-b border-border-color">
        <form onSubmit={handleSendRequest} className="flex space-x-2">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Add a friend by username"
            className="input flex-1"
          />
          <button
            type="submit"
            disabled={isLoadingRequest || !username.trim()}
            className="btn btn-primary flex items-center"
          >
            {isLoadingRequest ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </span>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Send
              </>
            )}
          </button>
        </form>
        
        {requestError && (
          <div className="mt-2 text-error text-sm flex items-center">
            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
            <span>{requestError}</span>
          </div>
        )}
        
        {error && (
          <div className="mt-2 text-error text-sm flex items-center">
            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
      
      <div className="border-b border-border-color">
        <div className="flex">
          <button
            className={`flex-1 py-2 text-center transition-colors ${
              activeTab === 'all' 
                ? 'text-text-primary border-b-2 border-accent-color' 
                : 'text-text-secondary hover:text-text-primary'
            }`}
            onClick={() => setActiveTab('all')}
          >
            All Friends ({friends.length})
          </button>
          <button
            className={`flex-1 py-2 text-center transition-colors ${
              activeTab === 'pending' 
                ? 'text-text-primary border-b-2 border-accent-color' 
                : 'text-text-secondary hover:text-text-primary'
            }`}
            onClick={() => setActiveTab('pending')}
          >
            Pending ({pendingRequests.length})
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <>
            <Skeleton className="w-full h-12 mb-4" />
            <Skeleton className="w-full h-12 mb-4" />
            <Skeleton className="w-full h-12" />
          </>
        ) : activeTab === 'all' ? (
          friends.length > 0 ? (
            <div className="space-y-3">
              {friends.map((friend) => (
                <div key={friend.id} className="card flex items-center justify-between animate-fade-in">
                  <div className="flex items-center">
                    <div className="avatar w-10 h-10 mr-3 flex-shrink-0">
                      {friend.friend?.avatar_url ? (
                        <img 
                          src={friend.friend.avatar_url} 
                          alt={friend.friend?.username} 
                          className="w-10 h-10 object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-accent-color text-white flex items-center justify-center rounded-full">
                          {friend.friend?.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-medium">{friend.friend?.username}</div>
                      <div className="text-xs text-text-secondary">
                        {friend.friend?.status || 'Offline'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      className="text-text-secondary hover:text-text-primary p-2 rounded-full transition-colors"
                      onClick={() => handleMessageFriend(friend.friend_id)}
                    >
                      <MessageSquare className="h-5 w-5" />
                    </button>
                    <button
                      className="text-text-secondary hover:text-error p-2 rounded-full transition-colors"
                      onClick={() => removeFriend(friend.friend_id)}
                    >
                      <UserMinus className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-text-secondary">
              <Users className="h-12 w-12 mb-4" />
              <p className="text-center">No friends yet</p>
              <p className="text-sm">Add friends to start chatting!</p>
            </div>
          )
        ) : (
          pendingRequests.length > 0 ? (
            <div className="space-y-3">
              {pendingRequests.map((request) => (
                <div key={request.id} className="card flex items-center justify-between animate-fade-in">
                  <div className="flex items-center">
                    <div className="avatar w-10 h-10 mr-3 flex-shrink-0">
                      {request.user?.avatar_url ? (
                        <img 
                          src={request.user.avatar_url} 
                          alt={request.user?.username} 
                          className="w-10 h-10 object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-accent-color text-white flex items-center justify-center rounded-full">
                          {request.user?.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-medium">{request.user?.username}</div>
                      <div className="text-xs text-accent-color">
                        Wants to add you as a friend
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      className="bg-success hover:bg-success/90 text-black p-2 rounded-full transition-colors"
                      onClick={() => acceptFriendRequest(request.id)}
                    >
                      <Check className="h-5 w-5" />
                    </button>
                    <button
                      className="bg-error hover:bg-error/90 text-white p-2 rounded-full transition-colors"
                      onClick={() => rejectFriendRequest(request.id)}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-text-secondary">
              <UserPlus className="h-12 w-12 mb-4" />
              <p className="text-center">No pending requests</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default FriendsList;